import { createAuth0Client } from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/vue';
import Vue from 'vue';

import { queryStringToObject } from '~/components/tool/Query';
import { UserRole } from '../components/settings/User';
import { isTokenExpired, getTokenRemainingTime } from '~/utils/tokenHelper';

/** Define a default action to perform after authentication */
// const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
/* eslint-disable prefer-const, @typescript-eslint/naming-convention */
export const useAuth0 = ({ onRedirectCallback, ...options }, context) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: null,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        token: null,
        isHandoffSession: false,
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created(): Promise<void> {
      // 外部アクセス用のページの場合は認証関連の初期化をスキップ
      if (context.route.path.startsWith('/external/')) {
        this.loading = false;
        return;
      }

      if (process.env.DEVELOPMENT_TYPE === 'static') {
        setTimeout(() => {
          this.isAuthenticated = true;
          this.token = 'test';
          this.getMe();
        }, 1000);
        setTimeout(() => {
          this.loading = false;
        }, 2000);
        return;
      }
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: options.redirectUri,
        },
        // scope: options.scope,
        useRefreshTokens: true, // トークン更新をリフレッシュトークンで行う
        cacheLocation: 'localstorage', // 格納先はlocalstorage（ページリロード時も保持されるため高速）
        sessionCheckExpiryDays: 365, // リフレッシュトークンの寿命をauth0の設定と合わせる
      });

      try {
        // If the user is returning to the app after authentication..
        if (
          window.location.search.includes('code=')
          && window.location.search.includes('state=')
        ) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback();

          this.error = null;

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        // リフレッシュトークンの有効性を確認する
        const isRefreshTokenValid = await this.auth0Client
          .getTokenSilently()
          .then(() => true)
          .catch((error) => {
            // Sentryに通知
            Sentry.captureException(
              `this.isAuthenticated判定前エラーが発生: ${error?.message}`,
            );
            return false;
          });
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        // リフレッシュトークンが有効、かつ、セッションが有効な場合は、アクセストークンを更新する
        if (this.isAuthenticated && isRefreshTokenValid) {
          this.token = await this.auth0Client
            .getTokenSilently()
            .catch((error) => {
              // Sentryに通知
              Sentry.captureException(
                `this.isAuthenticated判定後エラーが発生: ${error?.message}`,
              );
              // 一つ前のgetTokenSilentlyでエラーになっている可能性があるため、この処理でも念の為エラーをキャッチする
              // この処理でエラーが出た場合にログインフローが完全に止まってしまうため
              // https://doctors-jp.backlog.com/view/DXUT-257
            });

          await this.getMe();
          Sentry.setTag('userid', this.user.id);
          Sentry.setTag('email', this.user.mailaddress);

          this.loading = false;

          localStorage.setItem('role', this.user.role);
          const redirectPath = localStorage.getItem('auth_redirect');
          if (redirectPath) {
            localStorage.removeItem('auth_redirect');
            context.redirect(redirectPath);
          }
        } else if (new URLSearchParams(window.location.search).has('ht')) {
          // Android WebView から外部ブラウザに渡された handoff token で認証を引き継ぐ
          const htToken = new URLSearchParams(window.location.search).get('ht');

          try {
            const apiOrigin = context.env.API_ORIGIN || '';
            const res = await window.fetch(`${apiOrigin}/auth/handoff/exchange`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ handoff_token: htToken }),
            });
            if (!res.ok) throw new Error(`status ${res.status}`);
            const { access_token } = await res.json();
            // isHandoffSession を先にセットしないと getMe() 内の axios interceptor が
            // getTokenSilently() を呼んで失敗するため、順序を厳守する
            this.token = access_token;
            this.isHandoffSession = true;
            this.isAuthenticated = true;
            await this.getMe();
            localStorage.setItem('role', this.user.role);
            Sentry.setTag('userid', this.user.id);
            Sentry.setTag('email', this.user.mailaddress);
            // URL から ht パラメータを除去してブラウザ履歴に残さない
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('ht');
            window.history.replaceState({}, document.title, cleanUrl.toString());
          } catch (e) {
            console.error('[handoff] failed:', (e as Error).message, e);
            Sentry.captureException(`handoff token exchange failed: ${(e as Error).message}`);
            context.redirect('/login');
          }
          this.loading = false;
        } else if (
          // local 開発時はログインできるので loginWithRedirect 可
          process.env.NODE_ENV === 'development'
          // dstation.thesss.net へのアップ時以外は loginWithRedirect 可
          || process.env.DEVELOPMENT_TYPE !== 'static'
        ) {
          // ログイン不必要ページは loginWithRedirect しない
          if (
            !context.route.meta[0]?.permissions?.includes(UserRole.Guest)
            && !context.route.path.startsWith('/external/')
          ) {
            // DST-3730 セッション切れの際、localstorageの中身を確認して原因がDSにあるのかAuth0にあるのか確認する
            const storage = Object.entries(window.localStorage).reduce(
              (prev, current) => {
                const [key, value] = current;
                const next = prev;
                next[key] = value;
                return prev;
              },
              {},
            );
            Sentry.captureMessage('This user does not have a valid session.', {
              contexts: { storage },
            });
            //

            let role = options.redirectUri.split('role=')[1];
            role = role ? role.split('&')[0] : '';

            const redirectPath = context.route.fullPath;
            // 新規登録時保存されていたpathを上書きしないように設定
            if (
              !['/welcome-interview', '/welcome', '/'].includes(redirectPath)
            ) {
              localStorage.setItem('auth_redirect', redirectPath);
            }
            if (
              (!!role || !!localStorage.getItem('role'))
              && (role === UserRole.Doctor
                || localStorage.getItem('role') !== UserRole.Patient)
            ) {
              context.redirect(`/login?role=${UserRole.Doctor}`);
            } else if (options.redirectUri.includes('registration_info')) {
              const registrationInfo = options.redirectUri.split(
                'registration_info=',
              )[1];
              context.redirect(`/login?registration_info=${registrationInfo}`);
            } else {
              context.redirect('/login');
            }
          }
        }
      }
    },
    methods: {
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback(): Promise<void> {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          console.log('handleRedirectCallback', this.user);
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },

      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },

      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(o) {
        return this.auth0Client.getTokenSilently(o);
      },

      /** Logs the user out and removes their session on the authorization server */
      logout(o) {
        return this.auth0Client.logout(o);
      },

      async getMe(): Promise<void> {
        // eslint-disable-next-line no-async-promise-executor
        const user = await context.$api.getProfile();
        this.user = user;
      },

      /** アクセストークンを更新する。キャッシュが残っている場合はキャッシュを利用する */
      async updateTokenWithCache(): Promise<string> {
        const currentToken = this.token;
        const isExpired = isTokenExpired(currentToken);
        const remainingTime = getTokenRemainingTime(currentToken);

        // handoff session は Auth0 セッションがないためトークン更新不可
        if (this.isHandoffSession) {
          if (isExpired) {
            // トークン期限切れ時はWebViewから再入室してもらう
            Sentry.captureMessage(
              `handoff session トークン期限切れ userid: ${this.user?.id}`,
              'warning',
            );
            context.redirect('/login');
            throw new Error('handoff session token expired');
          }
          return this.token;
        }

        // トークンの有効期限チェックログ
        if (isExpired) {
          Sentry.captureMessage(
            `アクセストークン期限切れ検出 - リフレッシュトークンで更新開始 userid: ${this.user?.id} email: ${this.user?.mailaddress}`,
            'info',
          );
          Sentry.addBreadcrumb({
            category: 'auth',
            message:
              'アクセストークンが期限切れのためリフレッシュトークンで更新',
            level: 'info',
            data: {
              userId: this.user?.id,
              email: this.user?.mailaddress,
              wasExpired: true,
              remainingTime,
            },
          });
        }

        const previousToken = this.token;
        this.token = await this.auth0Client.getTokenSilently();

        // トークンが実際に更新されたかチェック
        if (previousToken !== this.token && isExpired) {
          Sentry.captureMessage(
            `リフレッシュトークンによる更新完了 userid: ${this.user?.id} email: ${this.user?.mailaddress}`,
            'info',
          );
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'リフレッシュトークンによるアクセストークン更新完了',
            level: 'info',
            data: {
              userId: this.user?.id,
              email: this.user?.mailaddress,
              newTokenRemainingTime: getTokenRemainingTime(this.token),
            },
          });
        }

        return this.token;
      },
      /** アクセストークンを更新する。キャッシュが残っている場合も強制的に更新する */
      async updateToken(): Promise<void> {
        const currentToken = this.token;
        const isExpired = isTokenExpired(currentToken);
        const remainingTime = getTokenRemainingTime(currentToken);

        // 強制更新時もログを出力
        Sentry.captureMessage(
          `アクセストークン強制更新開始 (期限切れ: ${isExpired}) userid: ${this.user?.id} email: ${this.user?.mailaddress}`,
          'info',
        );
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'アクセストークン強制更新',
          level: 'info',
          data: {
            userId: this.user?.id,
            email: this.user?.mailaddress,
            wasExpired: isExpired,
            remainingTime,
          },
        });

        this.token = await this.auth0Client.getTokenSilently({
          cacheMode: 'off',
        });

        Sentry.captureMessage(
          `アクセストークン強制更新完了 userid: ${this.user?.id} email: ${this.user?.mailaddress}`,
          'info',
        );
      },
    },
  });

  return instance;
};

export default async function (context, inject) {
  let redirectUri = window.location.origin;

  if (window && window.location.search) {
    const prams = queryStringToObject(window.location.search);

    if (prams?.role && prams?.role !== UserRole.Patient) {
      redirectUri += `?role=${UserRole.Doctor}`;
    }

    if (prams.registration_info) {
      redirectUri += `registration_info=${prams.registration_info}`;
    }
  }

  const options = {
    domain: context.env.AUTH0_DOMAIN,
    clientId: context.env.AUTH0_CLIENT_ID,
    audience: context.env.AUTH0_AUDIENCE,
    redirectUri,
    onRedirectCallback: (appState) => {
      context.app.router.push(
        appState && appState.targetUrl
          ? appState.targetUrl
          : window.location.pathname,
      );
    },
  };

  const $auth0 = await useAuth0({ ...options }, context);
  inject('auth0', $auth0);
}
